<script>
  import { groupsState, loadUserGroups } from '../../lib/stores/groups.svelte.js';
  import { getGroupBinders } from '../../lib/services/groups-api.js';
  import {
    activeSubgroupIds,
    groupUrlParams,
    selectedServerGroupId,
    syncSelectionFromRoute,
  } from '../../lib/stores/browse.svelte.js';
  import { route, navigate } from '../../lib/stores/router.svelte.js';
  import BinderCard from '../home/BinderCard.svelte';
  import NotSharedHere from '../shared/NotSharedHere.svelte';

  let loading = $state(true);
  let binders = $state([]);

  syncSelectionFromRoute(route.params);

  // The raw selected group id (not the resolved group object): available
  // from the first render, so a scoped deep link doesn't wait on
  // `groupsState.userGroups` to arrive before it can tell it's scoped.
  const groupId = $derived(selectedServerGroupId());
  // Conditional on groupId so the 'all'/'mine' view never reads
  // activeSubgroupIds() at all (it ignores the selection), and wrapped in
  // its own $derived -- not read straight from inside the $effect below --
  // so the effect's dependency is THIS derived's reference, not the raw
  // `groupsState.userGroups` signal activeSubgroupIds() touches internally.
  // activeSubgroupIds() is memoized in the store to return the SAME array
  // while the selection's content is unchanged, and a $derived only
  // propagates "changed" to its consumers (this effect) when its own
  // recomputed value differs by reference -- so an unchanged selection no
  // longer re-fires the fetch effect just because `groupsState.userGroups`
  // was touched (e.g. loadUserGroups() resolving after a cold deep link).
  const subgroupIds = $derived(groupId ? activeSubgroupIds() : []);

  // One card per Claim (ADR-0021 decision 6): keyed by Claimant + URL, so two
  // Claimants of one binder are two cards.
  function toCard(b) {
    return {
      key: `${b.claimant_uid}|${b.url}`,
      url: b.url,
      name: b.name,
      owner: b.member_name || b.owner || null,
      claimedAt: b.claimed_at ? new Date(b.claimed_at).getTime() : 0,
      shared: b.shared !== false,
    };
  }

  function byNewest(list) {
    return [...list].sort((a, b) => (b.claimedAt || 0) - (a.claimedAt || 0));
  }

  // One fetch path for both the scoped and 'all'/'mine' views, so `loading`
  // has a single owner and there's no window where one path's stale result
  // overwrites the other's in-flight state.
  $effect(() => {
    const gid = groupId;
    const ids = subgroupIds;
    let cancelled = false;
    loading = true;
    (async () => {
      try {
        let cards;
        if (gid) {
          // A selected server group: fetched server-side with the current
          // selection.
          const list = await getGroupBinders(gid, ids);
          cards = byNewest(list.map(toCard));
        } else {
          // 'all' / 'mine': every group's Claims, one card per Claim (a
          // Claim seen in several groups is one card, shared if it is
          // shared in any of them).
          if (!groupsState.loaded) await loadUserGroups();
          const results = await Promise.all(
            groupsState.userGroups.map((g) => getGroupBinders(g.groupId).catch(() => []))
          );
          const byClaim = new Map();
          for (const b of results.flat()) {
            const card = toCard(b);
            const seen = byClaim.get(card.key);
            if (!seen) byClaim.set(card.key, card);
            else if (card.shared) seen.shared = true;
          }
          cards = byNewest(byClaim.values());
        }
        if (!cancelled) binders = cards;
      } catch (err) {
        // Leave the previously rendered list alone on a transient failure —
        // blanking it to `binders = []` here would make a blip look like an
        // empty account. Both the scoped and all-groups branches rely on
        // this one handler now (the scoped branch no longer swallows its
        // own errors into `[]` before this catch can see them).
        console.error('[BindersPage] Failed to load binders:', err);
      } finally {
        if (!cancelled) loading = false;
      }
    })();
    return () => {
      cancelled = true;
    };
  });

  // Mirror the selection in the URL so a scoped view is shareable.
  $effect(() => {
    navigate('binders', groupUrlParams(), true);
  });
</script>

<main class="binders-page">
  <div class="binders-container">
    <div class="binders-header">
      <h2 class="binders-title">Trade Binders</h2>
      <p class="binders-summary">
        {binders.length} binder{binders.length !== 1 ? 's' : ''}
      </p>
    </div>

    {#if loading}
      <div class="binders-loading">Loading binders...</div>
    {:else if binders.length === 0}
      <div class="binders-empty">
        <p>No binders found. Open a binder to see it here.</p>
      </div>
    {:else}
      <ul class="binder-grid">
        {#each binders as binder, i (binder.key)}
          <BinderCard
            binder={{
              url: binder.url,
              name: binder.name || 'Unknown',
              owner: binder.owner,
            }}
            index={i}
            showMenu={false}
          >
            {#snippet badge()}
              {#if !binder.shared}<NotSharedHere class="binder-not-shared" />{/if}
            {/snippet}
          </BinderCard>
        {/each}
      </ul>
    {/if}
  </div>
</main>

<style>
  .binders-page {
    padding: var(--space-lg) var(--space-xl) 80px;
    max-width: 700px;
    margin: 0 auto;
    width: 100%;
  }

  .binders-container {
    width: 100%;
  }

  .binders-header {
    margin-bottom: var(--space-lg);
  }

  .binders-title {
    font-family: var(--font-serif);
    font-size: 1.625rem;
    font-weight: 700;
    color: var(--color-text);
    margin: 0 0 var(--space-xs);
  }

  .binders-summary {
    font-size: 13px;
    color: var(--color-text-tertiary);
    font-family: var(--font-mono);
    margin: 0;
  }

  .binders-loading,
  .binders-empty {
    text-align: center;
    color: var(--color-text-tertiary);
    padding: 48px 0;
    font-size: 14px;
  }

  /* Pinned to the card's top edge, like the User Page's corner counts. */
  .binder-grid :global(.binder-not-shared) {
    position: absolute;
    top: -9px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1;
  }

  /* Flat flex-wrap grid of binder chips, mirroring home page's RecentBinders */
  .binder-grid {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    gap: 1.5rem;
  }
</style>
